<template>
  <div class="cambiar-password-container min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
      <!-- Header -->
      <div class="px-8 py-6 bg-primary-600 dark:bg-primary-700">
        <div class="flex items-center justify-between">
          <h2 class="text-2xl font-bold text-white">Cambiar Contraseña</h2>
          <Icon icon="mdi:lock-reset" class="w-8 h-8 text-white opacity-80" />
        </div>
        <p class="mt-2 text-primary-100 text-sm">
          Asegura tu cuenta actualizando tu contraseña periódicamente.
        </p>
      </div>

      <!-- Formulario -->
      <form @submit.prevent="handleSubmit" class="p-8 space-y-6">

        <!-- Nueva Contraseña -->
        <div>
          <label for="new-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nueva Contraseña
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon icon="mdi:lock-outline" class="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="new-password"
              v-model="form.contrasenaNueva"
              :type="showNew ? 'text' : 'password'"
              required
              class="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm"
              placeholder="Mínimo 6 caracteres"
            />
            <button 
              type="button"
              @click="showNew = !showNew"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <Icon :icon="showNew ? 'mdi:eye-off' : 'mdi:eye'" class="h-5 w-5" />
            </button>
          </div>
        </div>

        <!-- Confirmar Contraseña -->
        <div>
          <label for="confirm-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Confirmar Nueva Contraseña
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon icon="mdi:lock-check-outline" class="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="confirm-password"
              v-model="form.confirmarContrasena"
              :type="showConfirm ? 'text' : 'password'"
              required
              class="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm"
              placeholder="Repite la nueva contraseña"
            />
            <button 
              type="button"
              @click="showConfirm = !showConfirm"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <Icon :icon="showConfirm ? 'mdi:eye-off' : 'mdi:eye'" class="h-5 w-5" />
            </button>
          </div>
        </div>

        <!-- Alerta de Error -->
        <div v-if="error" class="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-2 animate-shake">
          <Icon icon="mdi:alert-circle" class="w-5 h-5 text-red-500 mt-0.5" />
          <p class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
        </div>

        <!-- Alerta de Éxito -->
        <div v-if="success" class="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg flex items-start space-x-2 animate-fade-in">
          <Icon icon="mdi:check-circle" class="w-5 h-5 text-green-500 mt-0.5" />
          <p class="text-sm text-green-600 dark:text-green-400">{{ success }}</p>
        </div>

        <!-- Botones -->
        <div class="flex flex-col space-y-3 pt-4">
          <button
            type="submit"
            :disabled="loading || success"
            class="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon v-if="loading" icon="mdi:loading" class="animate-spin w-5 h-5 mr-2" />
            <span v-if="!success">Actualizar Contraseña</span>
            <span v-else>¡Contraseña Actualizada!</span>
          </button>
          
          <button
            type="button"
            @click="$router.back()"
            :disabled="loading"
            class="w-full flex justify-center items-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition-all"
          >
            Regresar
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { apiFetch } from '@/api/index.js';

export default {
  name: 'CambiarContrasena',
  components: {
    Icon
  },
  data() {
    return {
      form: {
        idUsuario: null,
        contrasenaNueva: '',
        confirmarContrasena: ''
      },
      showNew: false,
      showConfirm: false,
      loading: false,
      error: null,
      success: null
    };
  },
  created() {
    const userDataStr = localStorage.getItem('user') || localStorage.getItem('user_data');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      this.form.idUsuario = userData.idUsuario || userData.id;
    } else {
      this.$router.push('/');
    }
  },
  methods: {
    async handleSubmit() {
      this.error = null;
      this.success = null;

      // Validaciones básicas
      if (this.form.contrasenaNueva.length < 6) {
        this.error = "La nueva contraseña debe tener al menos 6 caracteres";
        return;
      }

      if (this.form.contrasenaNueva !== this.form.confirmarContrasena) {
        this.error = "Las contraseñas nuevas no coinciden";
        return;
      }

      this.loading = true;
      try {
        await apiFetch('/Usuario/cambiar-contrasena', {
          method: 'POST',
          body: JSON.stringify(this.form)
        });

        this.success = "¡Tu contraseña ha sido actualizada correctamente! Redirigiendo...";
        
        // Limpiar formulario
        this.form.contrasenaNueva = '';
        this.form.confirmarContrasena = '';

        // Redirigir después de 2 segundos
        setTimeout(() => {
          this.$router.push('/perfil');
        }, 2000);

      } catch (err) {
        console.error('❌ Error al cambiar contraseña:', err);
        this.error = err.message || "Ocurrió un error al intentar cambiar la contraseña";
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.animate-shake {
  animation: shake 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.4s ease-out;
}
</style>
